/**
 * Scrudix Error Tracker
 * 
 * Lightweight client-side error tracking that captures:
 *  - Unhandled exceptions (window.onerror)
 *  - Unhandled promise rejections
 *  - React ErrorBoundary reports
 *  - Manual error captures
 * 
 * Errors are batched and sent to the backend in bulk every 10 seconds.
 * In development mode, errors are only logged to console.
 */

interface ErrorReport {
  id: string;
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  col?: number;
  type: 'exception' | 'rejection' | 'boundary' | 'manual';
  url: string;
  userAgent: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

const MAX_QUEUE_SIZE = 50;
const FLUSH_INTERVAL = 10_000; // 10 seconds
const IS_DEV = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
import { useAuthStore } from './authStore';

let errorQueue: ErrorReport[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createReport(
  type: ErrorReport['type'],
  message: string,
  stack?: string,
  context?: Record<string, unknown>
): ErrorReport {
  return {
    id: generateId(),
    message: message.slice(0, 1000), // Cap message length
    stack: stack?.slice(0, 3000),
    type,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    context,
  };
}

function enqueue(report: ErrorReport) {
  // Log to console in all environments
  console.error(`[Scrudix ErrorTracker] ${report.type}:`, report.message, report.stack || '');

  try {
    window.dispatchEvent(
      new CustomEvent('scrudix:error', {
        detail: {
          id: report.id,
          type: report.type,
          message: report.message,
          timestamp: report.timestamp,
        },
      })
    );
  } catch {
    // Best-effort notification hook for UI listeners.
  }

  // In dev, just log — don't queue for backend
  if (IS_DEV) return;

  errorQueue.push(report);

  // Evict oldest if queue is full
  if (errorQueue.length > MAX_QUEUE_SIZE) {
    errorQueue = errorQueue.slice(-MAX_QUEUE_SIZE);
  }
}

async function flush() {
  if (errorQueue.length === 0) return;

  const batch = [...errorQueue];
  errorQueue = [];

  try {
    const token = useAuthStore.getState().token;
    await fetch('/api/errors/report', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ errors: batch }),
      // Don't let error reporting block the UI
      keepalive: true,
    });
  } catch {
    // Put failed reports back (once)
    errorQueue.unshift(...batch.slice(0, 20));
  }
}

/**
 * Initialize the error tracker. Should be called once at app startup.
 */
export function initErrorTracker() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Global unhandled errors
  window.addEventListener('error', (event) => {
    const report = createReport(
      'exception',
      event.message || 'Unknown error',
      event.error?.stack,
      { source: event.filename, line: event.lineno, col: event.colno }
    );
    report.source = event.filename;
    report.line = event.lineno;
    report.col = event.colno;
    enqueue(report);
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
        ? reason
        : 'Unhandled promise rejection';
    const stack = reason instanceof Error ? reason.stack : undefined;
    enqueue(createReport('rejection', message, stack));
  });

  // Periodic flush
  flushTimer = setInterval(flush, FLUSH_INTERVAL);

  // Flush on page unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });

  // Flush on beforeunload
  window.addEventListener('beforeunload', () => {
    flush();
  });

  console.log('[Scrudix ErrorTracker] Initialized');
}

/**
 * Manually capture an error (e.g., from ErrorBoundary or catch blocks).
 */
export function captureError(
  error: Error | string,
  type: ErrorReport['type'] = 'manual',
  context?: Record<string, unknown>
) {
  const message = error instanceof Error ? error.message : error;
  const stack = error instanceof Error ? error.stack : undefined;
  enqueue(createReport(type, message, stack, context));
}

/**
 * Capture a React ErrorBoundary error.
 */
export function captureBoundaryError(error: Error, componentStack?: string) {
  enqueue(
    createReport('boundary', error.message, error.stack, { componentStack })
  );
}

/**
 * Shutdown the error tracker.
 */
export function shutdownErrorTracker() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush(); // Final flush
  initialized = false;
}
